/**
 * warmupData — precarga en background los datos de TODOS los módulos apenas
 * el usuario entra (una vez por sesión de pestaña).
 *
 * Complemento de prefetchModules (que precarga el CÓDIGO de los módulos):
 * esto precarga sus DATOS en Redux con los mismos thunks y argumentos que
 * cada página despacha al montar. Al navegar, la página encuentra el store
 * ya poblado y renderiza al instante; su propio fetch de montaje sigue
 * corriendo en silencio y solo corrige si algo cambió en el backend
 * (stale-while-revalidate).
 *
 * Se lanza en olas escalonadas para no competir con los fetch del dashboard
 * (que el usuario está mirando) ni saturar la API.
 */
import dayjs from 'dayjs'

import { fetchBomberos } from '../store/slices/bomberosSlice'
import { fetchCitaciones } from '../store/slices/citacionesSlice'
import { fetchCargos, fetchEstadisticas as fetchCargosEstadisticas } from '../store/slices/cargosSlice'
import { fetchRamas } from '../store/slices/ramasSlice'
import { fetchTiposLicencia, fetchLicencias } from '../store/slices/licenciasSlice'
import { fetchMaterial, fetchMaterialAlertas } from '../store/slices/materialSlice'
import { fetchCategorias } from '../store/slices/categoriasSlice'
import { fetchCarrosAlertas, fetchCarros } from '../store/slices/carrosSlice'
import {
  fetchConfiguracion,
  fetchAñosDisponibles,
  fetchTiposCuota,
  fetchBomberosData,
  fetchExenciones,
  fetchMorosos,
  fetchRecaudacion,
} from '../store/slices/cuotasSlice'
import { fetchEspecialidades, fetchEspecialidadesStats } from '../store/slices/especialidadesSlice'
import { fetchTiposBombero } from '../store/slices/tiposBomberoSlice'
import { fetchOperadores } from '../store/slices/operadoresSlice'
import { fetchEstadoServicio } from '../store/slices/companiaServicioSlice'
import { fetchIntercambios, fetchGuardiaBomberos, fetchGuardiasMensuales, fetchEstadisticasGuardias, fetchConfigGuardia } from '../store/slices/guardiasSlice'
import { fetchNoLeidas, fetchNotificaciones } from '../store/slices/notificacionesSlice'
import { fetchCirculares, fetchCircularesStats } from '../store/slices/circularesSlice'
import { fetchGrilla, fetchClaves } from '../store/slices/cuadroListasSlice'
import { fetchCatalogos } from '../store/slices/clavesSlice'
import { fetchEmergencias } from '../store/slices/emergenciasSlice'

let iniciado = false

export function warmupData(dispatch) {
  if (iniciado || typeof window === 'undefined') return
  iniciado = true

  const año = dayjs().year()
  const mes = dayjs().month() + 1
  // Cuadro de Listas usa fechas UTC (ver CuadroListasPage); igualar para acertar la caché.
  const ahora = new Date()
  const cuadroAnio = ahora.getUTCFullYear()
  const cuadroMes = ahora.getUTCMonth() + 1

  // Olas de menor a mayor peso. Lo que el Dashboard ya pide al montar
  // (stats de bomberos/carros/material/citaciones, guardias del mes, cuotas
  // estadísticas, tipos de bombero, tendencias, notificaciones) NO se repite.
  const olas = [
    // Catálogos y listas principales
    [
      () => fetchBomberos({ page: 1, limit: 12, sortBy: 'fechaIngreso', sortOrder: 'asc' }), // args = montaje de BomberosPage (orden por antigüedad, 12/pág) → sin parpadeo de orden/cantidad
      () => fetchCitaciones({ page: 1, limit: 12 }),
      () => fetchCargos(),
      () => fetchRamas(),
      () => fetchTiposLicencia(),
      () => fetchClaves(), // catálogo de claves (Cuadro de Listas)
      () => fetchCatalogos(), // catálogos del módulo Claves radiales
    ],
    // Módulos de gestión
    [
      () => fetchLicencias({ mias: true }), // el módulo Licencias es PERSONAL ({mias:true}); con {} un admin precargaba las de TODA la dotación y se veían al entrar
      () => fetchMaterial({ limit: 50 }),
      () => fetchCategorias({ flat: true }),
      () => fetchMaterialAlertas(),
      () => fetchCarrosAlertas(),
      // La lista de carros (limit alto) es lo que Material Mayor espera al
      // montar; precalentarla evita el spinner frío de ~5s en esa página.
      () => fetchCarros({ page: 1, limit: 1000 }),
      () => fetchEspecialidades(),
      () => fetchEspecialidadesStats(),
      () => fetchTiposBombero(),
      () => fetchOperadores(),
      () => fetchEstadoServicio(),
      // Guardia Nocturna: dotación + mes actual + estadísticas (que la pestaña
      // "Calendario" y "Reemplazos" estén listas al entrar).
      () => fetchGuardiaBomberos(),
      () => fetchGuardiasMensuales({ mes, anio: año }),
      () => fetchEstadisticasGuardias({ anio: año, mes }),
      () => fetchConfigGuardia(),
      // Emergencias (partes de servicio) del mes — mismos args que la página al montar.
      () => fetchEmergencias({ anio: cuadroAnio, mes: cuadroMes }),
    ],
    // Tesorería (paquete del año en curso), vistas personales y módulos restantes
    [
      () => fetchConfiguracion(),
      () => fetchAñosDisponibles(),
      () => fetchTiposCuota({ año }),
      () => fetchBomberosData({ año, limit: 500 }),
      () => fetchExenciones({ año }),
      () => fetchMorosos({ año, limite: 100 }),
      () => fetchRecaudacion({ año }),
      () => fetchCargosEstadisticas(),
      () => fetchIntercambios(),
      () => fetchNoLeidas(),
      // Circulares (Diario Mural) y bandeja de notificaciones — listas completas.
      () => fetchCirculares({ page: 1, limit: 24, search: '', tipo: '' }),
      () => fetchCircularesStats(),
      () => fetchNotificaciones({ page: 1, limit: 20 }),
    ],
    // Cuadro de Listas: la grilla del mes se computa en el server (lo más pesado) → va al final.
    [
      () => fetchGrilla({ anio: cuadroAnio, mes: cuadroMes }),
    ],
  ]

  // Arranca 2s después del login (deja respirar a los fetch del dashboard)
  // y separa las olas 800ms entre sí.
  olas.forEach((ola, i) => {
    setTimeout(() => {
      ola.forEach((thunk) => {
        try {
          dispatch(thunk())
        } catch {
          // un fallo aislado no debe romper el warmup de los demás
        }
      })
    }, 2000 + i * 800)
  })
}
